import * as React from 'react';
import { Button, MessageBar, MessageBarBody, Spinner } from '@fluentui/react-components';
import { errorText, useApp } from '../app/context';
import { PRODUCT_NAME } from '../app/product';
import { resumeAfterSignIn, takeResume } from '../auth';
import { getBackground, grantSite, type IBackgroundInfo } from '../filing/background';

/**
 * Client uploads: links clients use to send documents straight into an application's folder. They
 * need our service to have write access to this one site, which only a SharePoint or Microsoft 365
 * admin can give (the same access filing in the background uses).
 */
export default function UploadsCard(): React.ReactElement | null {
  const app = useApp();
  const w = app.workspace;
  const [info, setInfo] = React.useState<IBackgroundInfo | undefined>(undefined);
  const [working, setWorking] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const resume = React.useRef<boolean>(!!takeResume('uploadsOn'));

  React.useEffect(() => {
    getBackground(w.driveId).then(setInfo).catch(() => setInfo({ available: false }));
  }, [w.driveId]);

  const turnOn = React.useCallback(async (): Promise<void> => {
    if (!info?.appId) {
      return;
    }
    setWorking(true);
    setError(undefined);
    try {
      if (!w.background?.enabled) {
        // The site's access is already there when filing in the background is on.
        resumeAfterSignIn({ kind: 'uploadsOn' });
        await grantSite(w, info.appId);
      }
      await app.saveWorkspace({ ...w, uploads: { enabled: true, since: new Date().toISOString(), by: app.account.username } });
      app.toast({ text: 'Client uploads are on. Create a link from an application\'s Requests tab.' });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(false);
    }
  }, [app, w, info]);

  React.useEffect(() => {
    if (resume.current && info?.appId) {
      resume.current = false;
      void turnOn();
    }
  }, [info, turnOn]);

  const turnOff = async (): Promise<void> => {
    setWorking(true);
    setError(undefined);
    try {
      await app.saveWorkspace({ ...w, uploads: undefined });
      app.toast({ text: 'Client uploads are off. Existing links stop working.' });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(false);
    }
  };

  if (!info) {
    return <section className="card"><Spinner size="tiny" label="Checking client uploads…" labelPosition="after" /></section>;
  }
  if (!info.available || !info.appId) {
    return null;
  }
  return (
    <section className="card" aria-labelledby="uploads-h" style={{ gap: 10 }}>
      <h2 id="uploads-h">Client uploads</h2>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {w.uploads?.enabled
        ? (
          <>
            <span className="small" style={{ fontSize: 14 }}>On. From an application's Requests tab, create a link to send the client. Their files go straight into the application's "From client" folder; {PRODUCT_NAME} never keeps a copy.</span>
            <Button disabled={working} onClick={() => { void turnOff(); }} style={{ alignSelf: 'flex-start' }}>Turn off</Button>
          </>
        )
        : (
          <>
            <span className="small" style={{ fontSize: 14 }}>
              Let clients send documents with a link, no account needed. Their files go straight into each application's folder in {w.siteName || 'this site'}.
              {!w.background?.enabled && <> A SharePoint or Microsoft 365 admin has to give {PRODUCT_NAME}'s service access to <strong>{w.siteName || 'this site'}</strong> only, nothing else in your organisation.</>}
            </span>
            <Button appearance="primary" disabled={working} onClick={() => { void turnOn(); }} style={{ alignSelf: 'flex-start' }}>{working ? 'Working…' : 'Turn on'}</Button>
          </>
        )}
    </section>
  );
}
